/**
 * Iter-66l: shared helper to group players for any picker / list UI.
 *
 * Sort order:
 *   1. Members by section (in the band's section sort order)
 *   2. Members with no section ("Unassigned (Members)")
 *   3. Deps by section
 *   4. Deps with no section ("Unassigned (Deps)")
 *
 * Within each section, players are sorted alphabetically by name (case-insensitive).
 *
 * @param {Array} players - raw player list from /api/players
 * @param {Array} sections - section list from /api/sections (for ordering)
 * @returns {Array<{key: string, label: string, type: 'member'|'dep', rows: Array}>}
 */
export function groupPlayersForPicker(players = [], sections = []) {
  const sectionOrder = sections.map((s) => s.id);

  const groupMap = new Map(); // composite key -> { key, label, type, rows[] }

  const keyFor = (type, sectionId, sectionName) => {
    if (sectionId) return `${type}__${sectionId}`;
    if (sectionName) return `${type}__name:${sectionName}`;
    return `${type}__unassigned`;
  };

  const labelFor = (type, sectionName) => {
    const suffix = type === "dep" ? " (Deps)" : "";
    if (!sectionName) return `Unassigned${suffix}`;
    return `${sectionName}${suffix}`;
  };

  const ensureGroup = (type, sectionId, sectionName) => {
    const k = keyFor(type, sectionId, sectionName);
    if (!groupMap.has(k)) {
      groupMap.set(k, {
        key: k,
        type,
        sectionId,
        sectionName: sectionName || null,
        label: labelFor(type, sectionName),
        rows: [],
      });
    }
    return groupMap.get(k);
  };

  for (const p of players) {
    const type = p.player_type === "dep" ? "dep" : "member"; // info_only, inactive collapse into 'member' for picker purposes
    const primaryId = (p.section_ids && p.section_ids[0]) || null;
    const primaryName = primaryId
      ? sections.find((s) => s.id === primaryId)?.name || (p.section_names || [])[0]
      : (p.section_names || [])[0];

    ensureGroup(type, primaryId, primaryName).rows.push(p);
  }

  const groups = Array.from(groupMap.values());

  // Sort groups: Members first (by section order), then Deps (by section order)
  groups.sort((a, b) => {
    // Members before Deps
    if (a.type !== b.type) return a.type === "member" ? -1 : 1;
    // Unassigned always at the bottom of its type
    const aUn = !a.sectionId && !a.sectionName;
    const bUn = !b.sectionId && !b.sectionName;
    if (aUn !== bUn) return aUn ? 1 : -1;
    // Known sections by band order
    const aIdx = a.sectionId ? sectionOrder.indexOf(a.sectionId) : -1;
    const bIdx = b.sectionId ? sectionOrder.indexOf(b.sectionId) : -1;
    if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
    if (aIdx !== -1) return -1;
    if (bIdx !== -1) return 1;
    return (a.sectionName || "").localeCompare(b.sectionName || "");
  });

  // Alphabetical within group
  groups.forEach((g) =>
    g.rows.sort((x, y) => (x.name || "").toLowerCase().localeCompare((y.name || "").toLowerCase()))
  );

  return groups;
}

/**
 * Filter grouped players by a free-text query (matches name, email,
 * section_names). Empty/falsy groups are dropped so the picker doesn't
 * render headings with zero rows.
 */
export function filterGroupedPlayers(groups, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return groups;
  return groups
    .map((g) => ({
      ...g,
      rows: g.rows.filter((p) => {
        const hay = [
          p.name,
          p.email,
          ...(p.section_names || []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      }),
    }))
    .filter((g) => g.rows.length > 0);
}
